import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Search, Users, Copy, GitMerge, ArrowUpDown, ArrowUp, ArrowDown,
  ChevronLeft, ChevronRight, Trash2, ChevronsLeft, ChevronsRight, RefreshCw,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { DateStamp, Person, QueryError, Status } from '@/workspace/ui';
import './directory-controls.css';

// Contacts table (Leads, Visitors) — the prototype's record table, plus this table's own search, sorting, paging and
// bulk delete. Dates follow the display timezone.

/* ── Sort icon ── */
const SortIcon = ({ col, sort }) => {
  if (sort.col !== col) return <ArrowUpDown size={12} className="sp-sort-idle" />;
  return sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
};

const SortColumn = ({ label, column, sort, onSort, numeric = false }) => (
  <th className={numeric ? 'num' : undefined} aria-sort={sort.col === column ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
    <button type="button" className="sp-sort" onClick={() => onSort(column)}>{label}<SortIcon col={column} sort={sort} /></button>
  </th>
);

const PAGE_SIZES = [8, 10, 20, 50];

export function DirectoryRefresh({ onRefresh, loading, label = 'Refresh records' }) {
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [failed, setFailed] = useState(false);
  const refresh = async () => {
    if (busy || loading || !onRefresh) return;
    setBusy(true); setFailed(false); setFeedback('');
    try {
      const request = onRefresh();
      const result = await request;
      if (result === false || result?.isError || result?.error) throw new Error('Refresh failed');
      setFeedback(request?.then || result === true || result?.isSuccess ? 'Updated just now' : 'Refresh requested');
    } catch {
      setFailed(true); setFeedback('Could not refresh. Try again.');
    } finally { setBusy(false); }
  };
  return <span className="sp-directory-refresh">
    <button type="button" className="btn" onClick={refresh} disabled={busy || loading || !onRefresh} aria-label={label} aria-busy={busy || loading || undefined}>
      <RefreshCw size={15} className={busy || loading ? 'sp-spin' : undefined} aria-hidden="true" /> {busy || loading ? 'Refreshing…' : 'Refresh'}
    </button>
    {(busy || loading || feedback) && <span className={failed ? 'sp-directory-error' : 'badge'} role={failed ? 'alert' : 'status'}>{busy || loading ? 'Refreshing…' : feedback}</span>}
  </span>;
}

export const ContactsTable = ({
  contacts,
  loading,
  initialLoad,
  error,
  onRetry,
  onSelectContact,
  onCopyScript,
  onBulkDelete,
  hideSearch,
  hasFilters = false,
  filterKey,
  onClearFilters,
  onSelectionChange,   // optional: (selectedIds: Set<string>) => void
}) => {
  const [search,   setSearch]   = useState('');
  const [selected, setSelected] = useState(new Set());
  const [sort,     setSort]     = useState({ col: 'updated_at', dir: 'desc' });
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deletePending = useRef(false);
  const [deleteError, setDeleteError] = useState('');
  const [copying, setCopying] = useState(false);
  const copyScript = async () => {
    if (copying || !onCopyScript) return;
    setCopying(true);
    try { await onCopyScript(); }
    catch { toast.error('Could not copy the tracking script. Check clipboard permissions and try again.'); }
    finally { setCopying(false); }
  };


  // Notify parent whenever selection changes
  useEffect(() => {
    if (onSelectionChange) onSelectionChange(selected);
  }, [selected, onSelectionChange]);
  useEffect(() => { setPage(1); }, [search, filterKey]);

  /* ── Filter ── */
  const filtered = useMemo(() => {
    if (hideSearch) return contacts;
    if (!search) return contacts;
    const q = search.trim().toLowerCase();
    return contacts.filter(c =>
      (c.name  && c.name.toLowerCase().includes(q)) ||
      (c.email && c.email.toLowerCase().includes(q)) ||
      (c.phone && c.phone.toLowerCase().includes(q))
    );
  }, [contacts, search, hideSearch]);

  // Keep a selection across pages and background refreshes, but never retain
  // IDs that are no longer in this filtered directory.
  useEffect(() => {
    const available = new Set(filtered.map(c => c.contact_id));
    setSelected(previous => {
      const next = new Set([...previous].filter(id => available.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [filtered]);

  /* ── Sort ── */
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { col, dir } = sort;
    arr.sort((a, b) => {
      let av = (col === 'attribution.utm_source' ? a.attribution?.utm_source : a[col]) ?? '';
      let bv = (col === 'attribution.utm_source' ? b.attribution?.utm_source : b[col]) ?? '';
      if (col === 'visit_count') { av = Number(av); bv = Number(bv); }
      else if (col === 'updated_at') { av = Date.parse(av) || 0; bv = Date.parse(bv) || 0; }
      else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ?  1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sort]);

  /* ── Pagination ── */
  const totalPages  = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage    = Math.min(page, totalPages);
  const sliced      = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);
  const [goInput,   setGoInput] = useState('');
  const [pageError, setPageError] = useState('');
  useEffect(() => { setPage(current => Math.min(current, totalPages)); }, [totalPages]);
  const goToPage = () => {
    const next = Number(goInput);
    if (!Number.isInteger(next) || next < 1 || next > totalPages) {
      setPageError(`Enter a whole page number between 1 and ${totalPages}.`);
      return;
    }
    setPage(next); setGoInput(''); setPageError('');
  };

  const toggleSort = (col) => {
    setSort(prev => prev.col === col
      ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' }
    );
    setPage(1);
  };

  /* ── Selection ── */
  const slicedIds    = sliced.map(c => c.contact_id);
  const allSelected  = slicedIds.length > 0 && slicedIds.every(id => selected.has(id));
  const someSelected = slicedIds.some(id => selected.has(id));
  const selectedCount = selected.size;

  const toggleAll = () => {
    setSelected(prev => {
      const n = new Set(prev);
      allSelected ? slicedIds.forEach(id => n.delete(id)) : slicedIds.forEach(id => n.add(id));
      return n;
    });
  };
  const toggleOne = (id, e) => {
    e.stopPropagation();
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const handleBulkDelete = async (event) => {
    event.preventDefault(); // Keep the confirmation mounted until the request succeeds.
    if (deletePending.current || !onBulkDelete || !selected.size) return;
    deletePending.current = true;
    const ids = [...selected];
    setDeleting(true);
    setDeleteError('');
    try {
      const result = await onBulkDelete(ids);
      if (result === false) throw new Error('Could not delete the selected contacts. Please try again.');
      const failedIds = result?.failedIds || [];
      const deletedIds = new Set(result?.deletedIds || ids.filter(id => !failedIds.includes(id)));
      setSelected(previous => new Set([...previous].filter(id => !deletedIds.has(id))));
      if (failedIds.length) {
        setDeleteError(`${failedIds.length} contact${failedIds.length === 1 ? '' : 's'} could not be deleted. They remain selected; try again.`);
      } else {
        setDeleteOpen(false);
      }
    } catch (error) {
      setDeleteError(error.message || 'Could not delete the selected contacts. Please try again.');
    } finally {
      deletePending.current = false;
      setDeleting(false);
    }
  };

  /* ── Pagination pages array ── */
  const pagesArr = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (safePage <= 4) return [1, 2, 3, 4, 5, '...', totalPages];
    if (safePage >= totalPages - 3) return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '...', safePage - 1, safePage, safePage + 1, '...', totalPages];
  }, [totalPages, safePage]);


  const showSkeletons = loading && initialLoad;

  if (error) return <QueryError onRetry={onRetry} message="Could not load contacts. Your data has not been changed." />;

  return (
    <div className="sp-contacts">
      {!hideSearch && (
        <div className="table-toolbar">
          <div className="table-tabs"><span className="muted" style={{ fontSize: 11 }}>{filtered.length.toLocaleString('en-US')} {search ? 'matching' : 'newest loaded'}</span></div>
          <div className="table-tools">
            <label className="search-input"><Search aria-hidden="true" /><span className="sr-only">Search contacts</span>
              <input data-testid="contacts-table-search-input" placeholder="Search by name, email or phone…"
                value={search} onChange={e => setSearch(e.target.value)} /></label>
          </div>
        </div>
      )}

      {selectedCount > 0 && (
        <div data-testid="bulk-action-bar" className="bulkbar">
          <strong>{selectedCount} selected</strong>
          <button type="button" className="btn ghost small" disabled={deleting} onClick={() => setSelected(new Set())}>Clear selection</button>
          {onBulkDelete && <AlertDialog open={deleteOpen} onOpenChange={open => { if (!deleting) { setDeleteOpen(open); setDeleteError(''); } }}>
            <AlertDialogTrigger asChild>
              <button type="button" data-testid="leads-bulk-delete-button" className="btn small danger">
                <Trash2 size={14} /> Delete {selectedCount}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="sp-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {selectedCount} contact{selectedCount !== 1 ? 's' : ''}?</AlertDialogTitle>
                <AlertDialogDescription>This permanently removes all {selectedCount} selected contacts across pages and their visit history.</AlertDialogDescription>
              </AlertDialogHeader>
              {deleteError && <p role="alert" className="sp-directory-error">{deleteError}</p>}
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deleting} className="sp-secondary-button">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="bulk-delete-confirm-button" disabled={deleting} onClick={handleBulkDelete} className="sp-danger-solid">
                  {deleting ? 'Deleting…' : `Delete ${selectedCount}`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>}
        </div>
      )}

      <div className="sp-directory-mobile-controls" role="group" aria-label="Contact list controls">
          <label className="sp-directory-select-page">
            <span className="sp-check"><Checkbox data-testid="contacts-mobile-select-page" checked={allSelected ? true : someSelected ? 'indeterminate' : false}
              onCheckedChange={toggleAll} disabled={!slicedIds.length || showSkeletons || deleting} aria-label="Select all contacts on this page" /></span>
            <span>Select page</span>
          </label>
          <span className="sp-directory-mobile-sort">
            <label className="select-wrap"><span className="sr-only">Sort contacts by</span>
              <select aria-label="Sort contacts by" value={sort.col} onChange={event => { setSort({ col: event.target.value, dir: 'asc' }); setPage(1); }}>
                <option value="name">Name</option><option value="email">Email</option><option value="updated_at">Last activity</option>
                <option value="attribution.utm_source">Source</option><option value="visit_count">Visits</option>
              </select>
            </label>
            <button type="button" className="btn ghost icon-btn" aria-label={sort.dir === 'asc' ? 'Sort descending' : 'Sort ascending'} onClick={() => toggleSort(sort.col)}>
              {sort.dir === 'asc' ? <ArrowUp size={15} /> : <ArrowDown size={15} />}
            </button>
          </span>
      </div>
      <div className="table-scroll">
        <table aria-label="Contacts" data-testid="contacts-table" className="data-table">
          <thead>
            <tr>
              <th style={{ width: 25 }}><span className="sp-check">
                <Checkbox data-testid="contacts-select-all-checkbox" checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  onCheckedChange={toggleAll} onClick={e => e.stopPropagation()} disabled={!slicedIds.length || showSkeletons || deleting} aria-label="Select all contacts on this page" />
              </span></th>
              <SortColumn label="Name" column="name" sort={sort} onSort={toggleSort} />
              <SortColumn label="Email" column="email" sort={sort} onSort={toggleSort} />
              <SortColumn label="Last activity" column="updated_at" sort={sort} onSort={toggleSort} />
              <SortColumn label="Source" column="attribution.utm_source" sort={sort} onSort={toggleSort} />
              <SortColumn label="Visits" column="visit_count" sort={sort} onSort={toggleSort} numeric />
            </tr>
          </thead>
          <tbody>
            {showSkeletons ? (
              Array.from({ length: pageSize }).map((_, i) => (
                <tr key={i} className="skeleton-row">
                  {Array.from({ length: 6 }).map((__, j) => <td key={j}><span /></td>)}
                </tr>
              ))
            ) : sliced.length === 0 ? (
              <tr><td colSpan={6}><div data-testid="contacts-empty-state" className="empty-state">
                <Users size={28} aria-hidden="true" />
                <h3>{search || hasFilters ? 'No contacts match your filters' : 'No contacts yet'}</h3>
                <p>{search || hasFilters ? 'Try a different search, source or date range.' : 'Add the tracking script to your page to start capturing leads.'}</p>
                {(search || hasFilters) && <button type="button" className="btn" onClick={() => { setSearch(''); onClearFilters?.(); }}>{hasFilters ? 'Clear filters' : 'Clear search'}</button>}
                {!search && !hasFilters && onCopyScript && (
                  <button type="button" data-testid="contacts-empty-state-copy-script" className="btn primary" onClick={copyScript} disabled={copying} aria-busy={copying || undefined}>
                    <Copy size={14} /> {copying ? 'Copying…' : 'Copy tracking script'}
                  </button>
                )}
              </div></td></tr>
            ) : (
              sliced.map(contact => {
                const src = contact?.attribution?.utm_source;
                const isSelected = selected.has(contact.contact_id);
                const label = contact.name || contact.email || 'Anonymous';
                const interactive = !!onSelectContact && !!contact.contact_id;
                return (
                  <tr data-testid="contacts-table-row" key={contact.contact_id} tabIndex={interactive ? 0 : undefined} data-interactive={interactive}
                    data-selected={isSelected || undefined}
                    onClick={interactive ? () => onSelectContact(contact.contact_id) : undefined}
                    onKeyDown={interactive ? e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onSelectContact(contact.contact_id); } } : undefined}
                    aria-label={interactive ? `View ${label}` : undefined}>
                    <td className="sp-check" onClick={e => toggleOne(contact.contact_id, e)}>
                      <Checkbox data-testid={`contact-checkbox-${contact.contact_id}`} checked={isSelected}
                        onCheckedChange={() => {}} onClick={e => toggleOne(contact.contact_id, e)} aria-label={`Select ${label}`} />
                    </td>
                    <td data-label="Name">
                      <Person name={contact.name} email={!contact.name ? contact.email : undefined} />
                      {contact.tags?.length > 0 && <span className="sp-contact-tags">{contact.tags.map(tag => <Status key={tag}>{tag}</Status>)}</span>}
                    </td>
                    <td data-label="Email">{contact.email || '—'}</td>
                    <td data-label="Last activity"><DateStamp value={contact.updated_at} /></td>
                    <td data-label="Source">{src ? <Status tone="blue">{src}</Status> : '—'}</td>
                    <td className="num" data-label="Visits">
                      <span className="sp-visits">
                        {contact.merged_children?.length > 0 && (
                          <span className="sp-merged" title={`${contact.merged_children.length} merged contact${contact.merged_children.length !== 1 ? 's' : ''}`}>
                            <GitMerge size={10} />{contact.merged_children.length}
                          </span>
                        )}
                        <strong className="sp-number">{contact.visit_count}</strong>
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!showSkeletons && sorted.length > 0 && (
        <div className="table-bottom sp-pager">
          <span className="sp-pager-info">
            Showing <strong>{((safePage - 1) * pageSize + 1).toLocaleString('en-US')}–{Math.min(safePage * pageSize, sorted.length).toLocaleString('en-US')}</strong> of <strong>{sorted.length.toLocaleString('en-US')}</strong>
            <label className="select-wrap"><span className="sr-only">Rows per page</span>
              <select data-testid="leads-filter-rows-per-page" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                {PAGE_SIZES.map(n => <option key={n} value={n}>Show {n} rows</option>)}
              </select></label>
          </span>
          <span className="pagination">
            <button type="button" onClick={() => setPage(1)} disabled={safePage === 1} aria-label="First page"><ChevronsLeft size={12} /></button>
            <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} aria-label="Previous page"><ChevronLeft size={12} /></button>
            {pagesArr.map((p, i) => (p === '...'
              ? <span key={`ellipsis-${i}`} className="muted">…</span>
              : <button type="button" key={p} className={safePage === p ? 'current' : ''} onClick={() => setPage(p)} aria-label={`Page ${p}`} aria-current={safePage === p ? 'page' : undefined}>{p}</button>))}
            <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} aria-label="Next page"><ChevronRight size={12} /></button>
            <button type="button" onClick={() => setPage(totalPages)} disabled={safePage === totalPages} aria-label="Last page"><ChevronsRight size={12} /></button>
          </span>
          <label className="sp-goto">Go to page
            <input type="number" min={1} max={totalPages} step={1} value={goInput} placeholder={String(safePage)} aria-invalid={!!pageError}
              onChange={e => { setGoInput(e.target.value); setPageError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); goToPage(); } }} /></label>
          <button type="button" className="btn small" onClick={goToPage} disabled={!goInput}>Go</button>
          {pageError && <span className="sp-directory-error" role="alert">{pageError}</span>}
        </div>
      )}
    </div>
  );
};
